import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Button } from '../../../components/button'
import Icon from '../../../components/icon'
import { ImageUploadPreviewModal } from '../../../components/modal/app_modals/ImagePreviewModal'
import { SelectProductModal } from '../../../components/modal/app_modals/selectProductModal'
import { removeElementAtIndex } from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import { Product } from '../../../redux-toolkits/products/products.type'
import {
  Section,
  SectionContent,
  Sections,
} from '../../../redux-toolkits/lists/list.types'

const insertTextAtCursor = (
  text: string,
  textarea?: HTMLTextAreaElement | null,
) => {
  if (textarea) {
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newValue =
      textarea.value.slice(0, start) + text + textarea.value.slice(end)

    return newValue
  }
}

export const ArticleSectionEditView = ({
  section,
  setActiveSectionIndex,
  sections,
  setSections,
  sectionIndex,
}: {
  section: Section
  setActiveSectionIndex: (index: number | null) => void
  sections: Sections
  setSections: (sections: Sections) => void
  sectionIndex: number
}) => {
  const [localSection, setLocalSection] = useState<Section>(section)
  const [allSectionsCopy, setAllSectionsCopy] = useState<Sections>(
    JSON.parse(JSON.stringify(sections)),
  )
  const [hasEmptyData, setHasEmptyData] = useState<boolean>(true)
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [activeTextArea, setActiveArea] = useState<HTMLTextAreaElement | null>(
    null,
  )
  const [activeTextAreaIndex, setActiveAreaIndex] = useState<number>()

  const inputsRef = useRef<HTMLTextAreaElement[]>([])
  const imageRef = useRef<HTMLInputElement | null>(null)

  const { handleModalClose, handleModalOpen, modal, modalOpen, setModalOpen } =
    useModalControl()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]
    setUploadedImage(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const moveContent = (index: number, direction: 'up' | 'down') => {
    const localSectionCopy: Section = JSON.parse(JSON.stringify(localSection))
    const targetIdx = direction === 'up' ? index - 1 : index + 1
    ;[localSectionCopy.content[index], localSectionCopy.content[targetIdx]] = [
      localSectionCopy.content[targetIdx],
      localSectionCopy.content[index],
    ]
    setLocalSection(localSectionCopy)
  }

  useEffect(() => {
    const hasEmpty =
      localSection?.content?.some((item) =>
        ['', null, undefined].includes(item.value),
      ) || (localSection?.content?.length ?? 0) === 0
    setHasEmptyData(hasEmpty)
  }, [localSection])

  useEffect(() => {
    setAllSectionsCopy(JSON.parse(JSON.stringify(sections)))
  }, [sections])

  return (
    <div className="border border-black rounded-md overflow-hidden">
      {/* Section header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-bg/50 border-b border-divider-gray">
        <p className="text-xs font-medium uppercase tracking-widest text-sec-black">
          Section {sectionIndex + 1}
        </p>
        <div className="flex items-center gap-1.5 text-xs text-sec-black">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          Editing
        </div>
      </div>

      {/* Content items */}
      <div className="divide-y divide-divider-gray">
        {localSection?.content?.map((sectionContent, index) => {
          const key = `content-${index}-${sectionContent.type}`
          return (
            <div className="flex gap-3 p-4" key={key}>
              <div className="flex-1">
                {sectionContent.type === 'text' ? (
                  <textarea
                    ref={(el) => {
                      if (el) {
                        inputsRef.current[index] = el
                      }
                    }}
                    onChange={(e) => {
                      const inputText = e.target.value
                      const localSectionCopy: Section = {
                        content: JSON.parse(
                          JSON.stringify(localSection.content),
                        ),
                      }
                      localSectionCopy.content[index].value = inputText

                      const productPlaceholders: string[] =
                        inputText.match(/__product_[a-f0-9]+__/g) || []

                      localSectionCopy.content[index].products =
                        localSectionCopy.content[index].products.filter(
                          (product) =>
                            productPlaceholders.includes(
                              `__product_${product._id}__`,
                            ),
                        )

                      setLocalSection(localSectionCopy)
                    }}
                    value={sectionContent.value}
                    rows={8}
                    placeholder="Write section content…"
                    className="
                      w-full bg-transparent outline-none resize-none
                      text-base leading-relaxed text-black
                      placeholder:text-sec-black/40
                    "
                  />
                ) : (
                  <img
                    src={sectionContent.value}
                    width={0}
                    height={0}
                    alt="article asset"
                    className="w-full max-w-[392px] aspect-square object-contain object-center rounded-lg !relative"
                  />
                )}
              </div>

              {/* Per-item actions */}
              <div className="flex flex-col gap-1.5 flex-shrink-0 pt-0.5">
                <button
                  type="button"
                  title="Move up"
                  disabled={index === 0}
                  onClick={() => moveContent(index, 'up')}
                  className="p-1.5 rounded-md text-sec-black hover:text-black hover:bg-gray-bg transition-colors disabled:opacity-30 disabled:cursor-not-allowed rotate-180"
                >
                  <Icon id="arrow-down-icon" width={14} height={14} />
                </button>

                <button
                  type="button"
                  title="Move down"
                  disabled={index === localSection.content.length - 1}
                  onClick={() => moveContent(index, 'down')}
                  className="p-1.5 rounded-md text-sec-black hover:text-black hover:bg-gray-bg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Icon id="arrow-down-icon" width={14} height={14} />
                </button>

                {sectionContent.type === 'text' && (
                  <button
                    type="button"
                    title="Insert product"
                    onClick={() => {
                      setActiveArea(inputsRef.current[index])
                      setActiveAreaIndex(index)
                      handleModalOpen('selectProductModal')
                    }}
                    className="p-1.5 rounded-md text-primary hover:bg-primary/10 transition-colors"
                  >
                    <Icon id="gifts-ion" width={16} height={16} />
                  </button>
                )}
                <button
                  type="button"
                  title="Remove block"
                  onClick={() => {
                    const localSectionCopy: Section = JSON.parse(
                      JSON.stringify(localSection),
                    )
                    const temp: SectionContent[] = removeElementAtIndex(
                      localSectionCopy.content,
                      index,
                    )
                    setLocalSection({ content: temp })
                  }}
                  className="p-1.5 rounded-md text-sec-black hover:bg-danger/10 hover:text-danger transition-colors"
                >
                  <Icon id="bin" width={16} height={16} />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-divider-gray bg-gray-bg/20">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="small"
            onClick={() => {
              const localSectionCopy: Section = {
                content: [
                  ...JSON.parse(JSON.stringify(localSection.content)),
                  { type: 'text', value: '', products: [] },
                ],
              }
              setLocalSection(localSectionCopy)
            }}
            label="Add text"
            icon="plus"
            iconSize={10}
            variant="border-grey"
          />

          <Button
            type="button"
            size="small"
            onClick={() => imageRef?.current?.click()}
            label="Add image"
            icon="plus"
            iconSize={10}
            variant="border-grey"
          />

          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="!hidden"
            ref={imageRef}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="small"
            onClick={() => {
              setSections(allSectionsCopy)
              setActiveSectionIndex(null)
            }}
            label="Cancel"
          />

          <Button
            type="button"
            size="small"
            onClick={() => {
              allSectionsCopy[sectionIndex] = localSection
              setSections(allSectionsCopy)
              setActiveSectionIndex(null)
            }}
            disabled={hasEmptyData}
            primary
            label="Done"
          />
        </div>
      </div>

      {uploadedImage && (
        <ImageUploadPreviewModal
          image={uploadedImage}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedImage(undefined)
          }}
          onSuccessCallback={(url) => {
            const localSectionCopy: Section = {
              content: [
                ...JSON.parse(JSON.stringify(localSection.content)),
                {
                  type: 'image',
                  value: url,
                  products: [],
                },
              ],
            }
            setLocalSection(localSectionCopy)
          }}
        />
      )}

      <SelectProductModal
        open={modal === 'selectProductModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        maxSelection={1}
        onSuccessCallback={(products: Product[]) => {
          const data = products[0]

          const updatedText =
            insertTextAtCursor(`__product_${data._id}__`, activeTextArea) ?? ''

          const localSectionCopy: Section = {
            content: JSON.parse(JSON.stringify(localSection.content)),
          }

          if (activeTextAreaIndex || activeTextAreaIndex === 0) {
            // set New value
            localSectionCopy.content[activeTextAreaIndex].value =
              updatedText ?? ''

            // Add new product to product list if product not already in list
            const existingProductIds = localSectionCopy.content[
              activeTextAreaIndex
            ].products.map((product) => product._id ?? product)

            if (!existingProductIds.includes(data._id)) {
              localSectionCopy.content[activeTextAreaIndex].products.push(data)
            }
          }

          setLocalSection(localSectionCopy)
          handleModalClose()
        }}
      />
    </div>
  )
}
